import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { DealInsuranceService } from "../../services";
import { InsuranceProduct, TermCost } from "../../models";
import { HistoryService } from "../../services/history.service";
import { FeatureFlag, FeatureFlagsService } from "../../services/feature-flags.service";

@Component({
  selector: "app-insurance-item-vsa",
  templateUrl: "./insurance-item-vsa.component.html",
  styleUrls: ["./insurance-item-vsa.component.scss"]
})
export class InsuranceItemVsaComponent implements OnChanges, OnInit {
  @Input() product: InsuranceProduct;
  @Input() selectedTermIndex = null;
  @Input() userSelection = false;
  @Input() vehicleCondition: string;
  @Output() closeModal = new EventEmitter();
  groupedTermCosts: { productName: string; termCosts: { termCost: TermCost; index: number }[] }[] = [];
  private uiState = {
    activeView: "whatisvsa",
    tabs: []
  };

  vsaForm: UntypedFormGroup = this.formBuilder.group({
    termCostIndex: [0]
  });

  newVSAFlowFlag = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private historyService: HistoryService,
    private featureFlagsService: FeatureFlagsService,
    private dealInsuranceService: DealInsuranceService,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.product || changes.vehicleCondition) {
      this.groupedTermCosts = this.buildGroupedTermCosts();
    }
  }

  ngOnInit() {
    this.syncSelectedPlan();
    this.vsaForm.valueChanges.subscribe(({termCostIndex}) => {
      this.product.userSelection = true;
      // Always convert from filtered index to actual index
      const actualIndex = this.getActualTermCostIndex(+termCostIndex);
      if (actualIndex >= 0) {
        this.dealInsuranceService.submitInsuranceProduct(this.product, actualIndex, true);
      }
    });

    this.newVSAFlowFlag = this.featureFlagsService.getFeatureFlag(FeatureFlag.NewVSAFlow);
    this.setTabs();
    this.featureFlagsService.onFlagUpdated(FeatureFlag.NewVSAFlow, (value) => {
      this.newVSAFlowFlag = value;
      this.setTabs();
    });
  }


  // INITIALIZATION

  syncSelectedPlan() {
    // Convert from original index to filtered index
    let filteredIndex = 0;
    if (!this.userSelection && !this.product.userSelection) {
      // user did not select this value, assume it was set as default, choose default value
      filteredIndex = this.getDefaultSelectionIndex();
    } else if (this.selectedTermIndex !== null && this.selectedTermIndex >= 0 && this.product?.termCosts) {
      const selectedTermCost = this.product.termCosts[this.selectedTermIndex];
      
      // Check if the selectedTermCost actually exists
      if (selectedTermCost) {
        const filteredTermCosts = this.termCosts;
        
        // Find the selected term cost in the filtered array
        const foundIndex = filteredTermCosts.findIndex(tc => 
          tc.term === selectedTermCost.term && 
          tc.miles === selectedTermCost.miles && 
          tc.price === selectedTermCost.price &&
          tc.productName === selectedTermCost.productName
        );
        
        if (foundIndex >= 0) {
          filteredIndex = foundIndex;
        } else {
          // No valid match found, use default
          filteredIndex = this.getDefaultSelectionIndex();
        }
      } else {
        // selectedTermIndex points to non-existent term cost, use default
        console.log(`VSA Modal: selectedTermIndex ${this.selectedTermIndex} has no term cost, using default`);
        filteredIndex = this.getDefaultSelectionIndex();
      }
    } else {
      // No selection at all, use default
      filteredIndex = this.getDefaultSelectionIndex();
    }
    
    this.vsaForm.patchValue({termCostIndex: filteredIndex});
  }
  
  private getDefaultSelectionIndex(): number {
    // For VSC products with groups, default to last item in first group
    if (this.product?.productKey === 'VSC' && this.hasProductNames()) {
      if (this.groupedTermCosts.length > 0 && this.groupedTermCosts[0].termCosts.length > 0) {
        const firstGroup = this.groupedTermCosts[0];
        const lastItemInFirstGroup = firstGroup.termCosts[firstGroup.termCosts.length - 1];
        console.log('VSA Modal: Defaulting to last item in first group', {
          groupName: firstGroup.productName,
          index: lastItemInFirstGroup.index
        });
        return lastItemInFirstGroup.index;
      }
    }
    return 0; // Default to first item if no groups
  }

  // ACTIONS

  onAcceptPlan() {
    const {termCostIndex} = this.vsaForm.value;
    this.historyService.dispatchAddEvent({
      shortName: "VSA Plan Accepted",
    })
    const actualIndex = this.getActualTermCostIndex(+termCostIndex);
    if (actualIndex >= 0) {
      this.dealInsuranceService.submitInsuranceProduct(this.product, actualIndex);
    }
    this.closeModal.emit();
  }

  onDeclinePlan() {
    this.historyService.dispatchAddEvent({
      shortName: "VSA Plan Declined",
    })
    this.dealInsuranceService.removeInsuranceProduct(this.product);
    this.closeModal.emit();
  }

  // UI CONTROL & RENDERING

  onSelectView(type: string) {
    this.uiState.activeView = type;
    let eventName: string = "";
    switch (type) {
      case "whatisvsa":
        eventName = "What is a VSA Tab Selected";
        break;
      case "options":
        eventName = "Options Tab Selected";
        break;
      case "repaircosts":
        eventName = "Repair Costs Tab Selected";
        break;
      case "vehicleDependability2020":
        eventName = "2020 Dependability Tab Selected";
        break;
      case "vehicleDependability2023":
        eventName = "2023 Dependability Tab Selected";
        break;
    }
    if (eventName) {
      this.historyService.dispatchAddEvent({
        shortName: eventName,
      });
    }
  }

  activeView(type: string): boolean {
    return this.uiState.activeView === type;
  }

  get viewTabs() {
    return this.uiState.tabs;
  }

  get termCosts(): TermCost[] {
    const terms = this.product ? this.product.termCosts : false;
    if (!terms) return [];
    
    // Filter out "Other" products
    const filteredTerms = terms.filter(tc => tc.productName !== 'Other');
    
    // If this is a VSC product with productNames, sort according to T1-T6 requirements
    if (this.product.productKey === 'VSC' && filteredTerms.some(tc => tc.productName)) {
      return this.sortVSCTermCosts(filteredTerms);
    }
    
    return filteredTerms;
  }
  
  private sortVSCTermCosts(termCosts: TermCost[]): TermCost[] {
    const getProductPriority = (tc: TermCost): number => {
      const productName = tc.productName || '';
      const normalized = productName.toLowerCase();
      const deductible = tc.deductible || 0;
      
      // Special sorting for certified vehicles
      if (this.vehicleCondition === 'certified') {
        if (normalized.includes('premier') && normalized.includes('deductible waiver') && !normalized.includes('flex')) return 1;
        if (normalized.includes('premier flex') && normalized.includes('deductible waiver') && !normalized.includes('certified')) return 2;
        if (normalized.includes('premier flex certified') && normalized.includes('deductible waiver')) return 3;
        if (normalized.includes('standard') && normalized.includes('deductible waiver')) return 4;
        return 99; // Other products shouldn't appear for certified vehicles
      }
      
      // Special sorting for used vehicles
      if (this.vehicleCondition === 'used') {
        if (normalized.includes('premier') && normalized.includes('deductible waiver') && !normalized.includes('flex')) return 1;
        if (normalized.includes('premier flex') && normalized.includes('deductible waiver')) return 2;
        if (normalized.includes('standard') && normalized.includes('deductible waiver')) return 3;
        if (normalized.includes('powertrain') && normalized.includes('deductible waiver')) return 4;
        return 99; // Other products shouldn't appear for used vehicles
      }
      
      // Build a sortable key based on product name and deductible for other vehicle types
      if (normalized.includes('premier') && normalized.includes('deductible waiver') && !normalized.includes('suspension')) return 1;
      if (normalized.includes('premier') && normalized.includes('suspension') && normalized.includes('deductible waiver')) return 2;
      if (normalized.includes('premier') && deductible === 50) return 3;
      if (normalized.includes('premier') && deductible === 100) return 4;
      if (normalized.includes('standard') && normalized.includes('deductible waiver')) return 5;
      if (normalized.includes('standard') && deductible === 50) return 6;
      if (normalized.includes('standard') && deductible === 100) return 7;
      if (normalized.includes('premier flex certified')) return 8;
      if (normalized.includes('premier flex')) return 9;
      
      return 99;
    };
    
    return termCosts.sort((a, b) => {
      // First sort by product priority
      const priorityA = getProductPriority(a);
      const priorityB = getProductPriority(b);
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Then by term
      if (a.term !== b.term) {
        return a.term - b.term;
      }
      
      // Then by miles
      return a.miles - b.miles;
    });
  }

  planSelected(): boolean {
    const {termCostIndex} = this.vsaForm.value;
    const actualIndex = this.getActualTermCostIndex(+termCostIndex);
    return this.selectedTermIndex === actualIndex;
  }

  noPlanSelected(): boolean {
    return this.selectedTermIndex === null;
  }
  
  hasProductNames(): boolean {
    return this.product?.termCosts?.some(tc => tc.productName) || false;
  }
  
  private buildGroupedTermCosts(): { productName: string; termCosts: { termCost: TermCost; index: number }[] }[] {
    if (!this.product?.termCosts) {
      return [];
    }
    
    const groups = new Map<string, { termCost: TermCost; index: number }[]>();
    const filteredTermCosts = this.termCosts; // This already has filtering and sorting applied
    
    // Use index from the filtered array
    filteredTermCosts.forEach((termCost, index) => {
      const productName = termCost.productName || 'Other';
      
      // Skip 'Other' products
      if (productName === 'Other') {
        return;
      }
      
      let groupKey = productName;
      
      // Add deductible to group name if not already included
      if ((productName.toLowerCase().includes('premier') || productName.toLowerCase().includes('standard')) 
          && !productName.includes('$') 
          && termCost.deductible > 0) {
        groupKey = `${productName} - $${termCost.deductible} Deductible`;
      }
      
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      
      groups.get(groupKey).push({ termCost, index });
    });
    
    // Convert to array and maintain the sort order from termCosts getter
    return Array.from(groups.entries())
      .map(([productName, termCosts]) => ({
        productName,
        termCosts
      }));
  }

  trackByGroup(_index: number, group: { productName: string }): string {
    return group.productName;
  }

  trackByTerm(_index: number, item: { index: number }): number {
    return item.index;
  }

  private getActualTermCostIndex(filteredIndex: number): number {
    // Get the filtered/sorted term costs
    const filteredTermCosts = this.termCosts;
    
    // If index is out of bounds, return -1
    if (filteredIndex < 0 || filteredIndex >= filteredTermCosts.length) {
      return -1;
    }
    
    // Get the selected term cost from filtered array
    const selectedTermCost = filteredTermCosts[filteredIndex];
    
    // Find this term cost in the original array
    const actualIndex = this.product.termCosts.findIndex(tc => 
      tc.term === selectedTermCost.term && 
      tc.miles === selectedTermCost.miles && 
      tc.price === selectedTermCost.price &&
      tc.productName === selectedTermCost.productName
    );
    
    return actualIndex;
  }

  private setTabs() {
    if (this.newVSAFlowFlag) {
      this.uiState.activeView = "whatisvsa";
      this.uiState.tabs = [
        {title: "What is a VSA?", id: "whatisvsa"},
        {title: "Options", id: "options"},
        {title: "Repair Costs", id: "repaircosts"},
        {title: "2021 Vehicle Dependability", id: "vehicleDependability2021"},
        {title: "2025 Vehicle Dependability", id: "vehicleDependability2025"},
        {title: "2026 Vehicle Dependability", id: "vehicleDependability2026"},
      ]
    } else {
      this.uiState.activeView = "comparison";
      this.uiState.tabs = [
        {title: "Comparison", id: "comparison"},
        {title: "2021 Vehicle Dependability", id: "vehicleDependability2021"},
        {title: "2025 Vehicle Dependability", id: "vehicleDependability2025"},
      ]
    }
  }
}
